import { useCallback } from 'react';

import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';
import { PaymentState } from '@sg/shared-fe/features/ordering';

import { type OrderingCart, sumLedgerTotal, type WantedTime } from '@order/features/ordering';
import { StripeLogger, useNotifyStripeError } from '@order/integrations/stripe';
import { useTelemetry } from '@order/Telemetry';

import { useNotificationType } from '../../../../integrations';
import { getDeliveryParams } from '../../utils';
import { useCreatePaymentIntent } from '../useCreatePaymentIntent';

export const usePaymentIntentCreation = (props: UsePaymentIntentCreationParams) => {
  const {
    wantedTime,
    selectedTip,
    cart,
    availableCredit,
    shouldUseCredit,
    dropoffLocation,
    shouldIncludeUtensils,
    createPayment,
    failPayment,
    changePaymentMethod,
    cancelRequest,
  } = props;

  const { track } = useTelemetry();
  const getNotificationType = useNotificationType();
  const createPaymentIntentMutation = useCreatePaymentIntent();
  const notifyStripeError = useNotifyStripeError();

  const createPaymentIntent = useCallback(
    async (
      paymentMethodId: string,
      paymentMethodType: string,
      shouldSetupFutureUsage?: boolean,
    ) => {
      try {
        const {
          orderId = '',
          orderChannel = 'pickup',
          restaurantEntityId = '',
          deliveryOrderDetail,
          canTrackOrderStatus,
          ledger,
        } = cart;

        const wantedTimeString = wantedTime?.time ?? '';
        const deliveryParams = getDeliveryParams(orderChannel, selectedTip, deliveryOrderDetail); // prettier-ignore
        const { notificationType = '', reason: notificationTypeReason } = getNotificationType({ canTrackOrderStatus, wantedTime: wantedTimeString }); // prettier-ignore
        const amount = sumLedgerTotal(ledger, selectedTip, availableCredit, shouldUseCredit).total; // prettier-ignore

        const params = {
          paymentMethodId,
          amount,
          orderId,
          orderType: OrderTypeValues.GravyOrder,
          restaurantEntityId,
          deliveryOrderDetail: deliveryParams,
          dropoffLocationId: dropoffLocation,
          includeUtensils: shouldIncludeUtensils,
          notificationType,
          pickupTip: selectedTip,
          useCredit: shouldUseCredit,
          wantedTime: wantedTimeString,
          shouldSetupFutureUsage,
        };

        track('checkout.stripe-usage', { event: 'create_payment_intent', paymentMethodType, params }); // prettier-ignore
        track('notification_preferences.order_notification_type', {
          orderId,
          notificationType,
          notificationTypeReason,
        });

        const response = await createPaymentIntentMutation(params);
        const { paymentIntentId, clientSecret } = response;

        changePaymentMethod(paymentIntentId);

        createPayment({
          paymentIntentId,
          paymentStateExpiration: Date.now(),
          paymentStateStatus: OrderPaymentStatus.Created,
          paymentStateError: undefined,
        });

        track('checkout.stripe-usage', { event: 'confirm_payment_intent', paymentIntentId }); // prettier-ignore

        return { orderId, paymentIntentId, clientSecret };
      } catch (error) {
        StripeLogger.logGenericError(error as Error);
        cancelRequest(true);
        failPayment();
        notifyStripeError((error as Error).message);
      }
    },
    [
      notifyStripeError,
      availableCredit,
      cancelRequest,
      cart,
      createPayment,
      failPayment,
      changePaymentMethod,
      createPaymentIntentMutation,
      dropoffLocation,
      getNotificationType,
      selectedTip,
      shouldIncludeUtensils,
      shouldUseCredit,
      track,
      wantedTime?.time,
    ],
  );

  return { createPaymentIntent };
};

type UsePaymentIntentCreationParams = {
  wantedTime: WantedTime | undefined;
  selectedTip: number;
  cart: OrderingCart;
  availableCredit: number;
  shouldUseCredit: boolean;
  dropoffLocation: string | undefined;
  shouldIncludeUtensils: boolean;
  createPayment: (paymentState: PaymentState) => void;
  failPayment: () => void;
  changePaymentMethod: (paymentMethodId: string) => void;
  cancelRequest: (shouldCancel: boolean) => void;
};
